import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { EGoalType, EGoalTypeV1, IPredicate, Segment } from '@feathr/blackbox';
import { EGoalType as GoalType } from '@feathr/blackbox';
import { Skeleton, Text } from '@feathr/components';
import type { AuthenticatedStore } from '@feathr/report_components/state/useAuthenticatedStore';
import type { ReportStore } from '@feathr/report_components/state/useReportStore';
import { isAuthenticatedStore } from '@feathr/report_components/state/useStore';
import useStore from '@feathr/report_components/state/useStore';

interface IProps {
  target: string;
  type: EGoalType | EGoalTypeV1;
}

function extractFormIds(predicates: IPredicate[]): string[] | undefined {
  for (const predicate of predicates) {
    if (predicate.group) {
      for (const groupPred of predicate.group) {
        if (groupPred.attr_against === 'form_id' && groupPred.comparison === 'in') {
          if (Array.isArray(groupPred.value)) {
            return groupPred.value as string[];
          }
        }
      }
    }
  }
  return undefined;
}

function PageViewTarget({ segment }: Readonly<{ segment: Segment }>): JSX.Element {
  const predicates = segment.get('predicates');
  const url = predicates?.[0]?.value;

  if (typeof url === 'string' && url) {
    return (
      <Text.DefaultLink href={url} target={'_blank'}>
        {url}
      </Text.DefaultLink>
    );
  }
  return <Text.DefaultRegular>-</Text.DefaultRegular>;
}

function SegmentNameTarget({ segment }: Readonly<{ segment: Segment }>): JSX.Element {
  const name = segment.get('name');
  return <Text.DefaultRegular>{name || '-'}</Text.DefaultRegular>;
}

const FormTarget = observer(function FormTarget({
  allLabel,
  segment,
  store,
  type,
}: Readonly<{
  allLabel: string;
  segment: Segment;
  store: ReportStore | AuthenticatedStore;
  type: EGoalType;
}>): JSX.Element {
  const predicates = segment.get('predicates');
  const formIds = extractFormIds(predicates);

  if (!formIds || formIds.length === 0) {
    return <Text.DefaultRegular>{allLabel}</Text.DefaultRegular>;
  }

  if (!isAuthenticatedStore(store)) {
    return <SegmentNameTarget segment={segment} />;
  }

  const isDonation = type === GoalType.donation_form_submission;

  if (isDonation) {
    const forms = formIds.map((id) => store.DonationForms.get(id));
    if (forms.some((form) => form.isPending)) {
      return <Skeleton />;
    }
    const validForms = forms.filter((form) => form.get('name'));
    if (validForms.length === 0) {
      return <Text.DefaultRegular>-</Text.DefaultRegular>;
    }
    return (
      <>
        {validForms.map((form, index) => (
          <React.Fragment key={form.id}>
            {index > 0 && ', '}
            <Text.DefaultLink to={form.getItemUrl()}>{form.get('name')}</Text.DefaultLink>
          </React.Fragment>
        ))}
      </>
    );
  }

  const forms = formIds.map((id) => store.Forms.get(id));
  if (forms.some((form) => form.isPending)) {
    return <Skeleton />;
  }
  const validForms = forms.filter((form) => form.get('name'));
  if (validForms.length === 0) {
    return <Text.DefaultRegular>-</Text.DefaultRegular>;
  }
  return (
    <>
      {validForms.map((form, index) => (
        <React.Fragment key={form.id}>
          {index > 0 && ', '}
          <Text.DefaultLink to={form.getItemUrl()}>{form.get('name')}</Text.DefaultLink>
        </React.Fragment>
      ))}
    </>
  );
});

function GoalsTargetCell({ target, type }: Readonly<IProps>): JSX.Element {
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        const store = useStore();
        const { t } = useTranslation();
        const segment = store.Segments.get(target);

        if (segment.isPending) {
          return <Skeleton />;
        }

        switch (type) {
          case GoalType.page_view:
            return <PageViewTarget segment={segment} />;

          case GoalType.group_entry:
            return (
              <Text.DefaultLink to={segment.getItemUrl()}>
                {segment.get('name') || '-'}
              </Text.DefaultLink>
            );

          case GoalType.conversion_pixel:
            return (
              <Text.DefaultLink to={'/data/pixel/conversions'}>
                {segment.get('name') || '-'}
              </Text.DefaultLink>
            );

          case 'ad_click':

          case 'custom':
            return <SegmentNameTarget segment={segment} />;

          case GoalType.donation_form_submission:
            return (
              <FormTarget
                allLabel={t('All donation forms')}
                segment={segment}
                store={store}
                type={type}
              />
            );

          case GoalType.form_submission:
            return (
              <FormTarget allLabel={t('All forms')} segment={segment} store={store} type={type} />
            );

          default:
            return <Text.DefaultRegular>-</Text.DefaultRegular>;
        }
      }}
    </Observer>
  );
}

export default GoalsTargetCell;
